import React, { useState, useEffect } from 'react';
import LoadingSpinner from './LoadingSpinner';
import { NumberField } from '@adobe/react-spectrum';
import fetchWithToken from '../services';

const SetTip = () => {
    const [locale, setLocale] = useState('en-US');
    const [currency, setCurrency] = useState('USD');
    const [divisor, setDivisor] = useState(null);
    const [absoluteMinimum, setAbsoluteMinimum] = useState(null);
    const [savedSuggestedTip, setSavedSuggestedTip] = useState(null);
    const [inputSuggestedTip, setInputSuggestedTip] = useState(null);
    const [savedMinTip, setSavedMinTip] = useState(null);
    const [inputMinTip, setInputMinTip] = useState(null);
    const [isSuggestedTipDirty, setIsSuggestedTipDirty] = useState(false);
    const [isMinTipDirty, setIsMinTipDirty] = useState(false);
    const [updatingSuggestedTip, setUpdatingSuggestedTip] = useState(false);
    const [updatingTips, setUpdatingTips] = useState(false);
    const [updatingMinTip, setUpdatingMinTip] = useState(false);
    const [tipError, setTipError] = useState('');
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

    // Detect if user is on desktop or smaller screen
    useEffect(() => {
        const handleResize = () => {
            setIsDesktop(window.innerWidth >= 768);
        };

        // Set initial value
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Clean up
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const updateTipAmounts = async (suggestedTip, minimumTip) => {
        try {
            await fetchWithToken('/update-tip-amounts', {
                method: 'POST',
                body: JSON.stringify({
                    suggestedTip,
                    minimumTip
                })
            });
            return true;
        } catch (error) {
            console.error('Error updating tip amounts:', error);
            return false;
        }
    };

    useEffect(() => {
        if (inputSuggestedTip !== savedSuggestedTip) {
            setIsSuggestedTipDirty(true);
        } else {
            setIsSuggestedTipDirty(false);
        }
    }, [inputSuggestedTip, savedSuggestedTip]);

    useEffect(() => {
        if (inputMinTip !== savedMinTip) {
            setIsMinTipDirty(true);
        } else {
            setIsMinTipDirty(false);
        }
    }, [inputMinTip, savedMinTip]);

    useEffect(() => {
        const getTipAmounts = async () => {
            try {
                const response = await fetchWithToken('/get-tip-amounts', {
                    method: 'GET'
                });
                const data = await response.json();

                console.log('getTip data: ', data);

                const { locale, currency, divisor, absoluteMinimum, defaultSuggested, minimumTip, suggestedTip } = data;

                let suggestedTipMainUnit;
                let minimumTipMainUnit;

                if (minimumTip === 0 || suggestedTip === 0) {
                    suggestedTipMainUnit = defaultSuggested / divisor;
                    minimumTipMainUnit = absoluteMinimum / divisor;
                    try {
                        await updateTipAmounts(defaultSuggested, absoluteMinimum);
                    } catch { }
                } else {
                    suggestedTipMainUnit = suggestedTip / divisor;
                    minimumTipMainUnit = minimumTip / divisor;
                }

                const absoluteMinimumMainUnit = absoluteMinimum / divisor;

                setLocale(locale);
                setCurrency(currency);
                setDivisor(divisor);
                setAbsoluteMinimum(absoluteMinimumMainUnit);
                setSavedSuggestedTip(suggestedTipMainUnit);
                setSavedMinTip(minimumTipMainUnit);
                setInputSuggestedTip(suggestedTipMainUnit);
                setInputMinTip(minimumTipMainUnit);
            } catch (error) {
                console.error('Error: ', error);
            }
        }
        getTipAmounts();
    }, []);

    useEffect(() => {
        console.log('suggested tip: ', savedSuggestedTip, 'min tip: ', savedMinTip);
    }, [savedSuggestedTip, savedMinTip]);

    useEffect(() => {
        if (inputMinTip < absoluteMinimum) {
            setTipError(`Minimum tip must be at least ${Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(absoluteMinimum)}`);
        } else if (inputMinTip > inputSuggestedTip) {
            setTipError('Minimum tip must be equal to or less than suggested tip');
        } else if (inputSuggestedTip < inputMinTip) {
            setTipError('Suggested tip must be larger than minimum tip');
        } else {
            setTipError('');
        }
    }, [inputSuggestedTip, inputMinTip, absoluteMinimum, locale, currency]);

    const handleSuggestedTipChange = (newValue) => {
        setInputSuggestedTip(newValue);
    };

    const handleMinTipChange = (newValue) => {
        setInputMinTip(newValue);
    };

    const handleConfirm = async () => {
        setUpdatingTips(true);
        const suggestedTip = inputSuggestedTip * divisor;
        const minimumTip = inputMinTip * divisor;
        try {
            const success = await updateTipAmounts(suggestedTip, minimumTip);
            if (success) {
                setSavedSuggestedTip(inputSuggestedTip);
                setSavedMinTip(inputMinTip);
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setUpdatingTips(false);
        }
    };

    return (
        <div className="w-full flex justify-center">
            {/* <div className="w-full max-w-full md:w-auto grid grid-cols-[2fr_3fr] md:grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-start bg-white rounded-lg p-4 shadow"> */}
            <div className="w-full max-w-full md:w-auto md:grid md:grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-start bg-white rounded-lg p-4 shadow">
                <label className="mb-1 text-right self-center text-black" htmlFor="suggested-tip">
                    Suggested tip:
                </label>
                <div className="max-md:pt-2 max-md:flex max-md:justify-center max-md:pb-4">
                    {isDesktop ?
                        <NumberField
                            value={inputSuggestedTip || ''}
                            onChange={handleSuggestedTipChange}
                            minValue={0}
                            formatOptions={{
                                style: 'currency',
                                currency: currency.toUpperCase()
                            }}
                            width={'110px'}
                            aria-label="Suggested tip"
                        />
                        :
                        <NumberField
                            value={inputSuggestedTip || ''}
                            onChange={handleSuggestedTipChange}
                            minValue={0}
                            formatOptions={{
                                style: 'currency',
                                currency: currency.toUpperCase()
                            }}
                            width={'190px'}
                            aria-label="Suggested tip"
                        />}
                </div>
                <label className="text-right self-center mb-1 text-black" htmlFor="min-tip">
                    Minimum tip:
                </label>
                <div className="max-md:pt-2 max-md:flex max-md:justify-center">
                    {isDesktop ?
                        <NumberField
                            value={inputMinTip || ''}
                            onChange={handleMinTipChange}
                            minValue={0}
                            formatOptions={{
                                style: 'currency',
                                currency: currency.toUpperCase()
                            }}
                            width={'110px'}
                            aria-label="Minimum tip"
                        />
                        :
                        <NumberField
                            value={inputMinTip || ''}
                            onChange={handleMinTipChange}
                            minValue={0}
                            formatOptions={{
                                style: 'currency',
                                currency: currency.toUpperCase()
                            }}
                            width={'190px'}
                            aria-label="Minimum tip"
                        />}
                </div>

                <div className="text-red-500 text-xs text-center min-h-[1rem] flex items-center justify-center" style={{ gridColumn: '1 / -1' }}>
                    {tipError && <p>{tipError}</p>}
                </div>
                <div style={{ gridColumn: '1 / -1' }} className="flex justify-center">
                    <button
                        className={`px-2 py-1 rounded text-white ${((isSuggestedTipDirty || isMinTipDirty) && !updatingSuggestedTip && !tipError) ? 'bg-green-500' : 'bg-gray-400'
                            }`}
                        onClick={handleConfirm}
                        disabled={updatingTips || (!isSuggestedTipDirty && !isMinTipDirty) || tipError}
                    >
                        {updatingTips ? (
                            <>
                                Updating
                                <LoadingSpinner className="inline-block ml-2 w-4 h-4" />
                            </>
                        ) : (
                            'Update'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SetTip;